import { useState } from "react";
import { useNotify } from "ra-core";
import { Trash2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

import { pruefe, sb } from "./daten";
import { pauschalenRechnen } from "./kern/rechnen";
import type { TagArt } from "./kern/typen";
import {
  Abschnitt,
  Auswahl,
  Feld,
  Hinweis,
  Tabelle,
  Zahl,
  fmtCent,
  fmtDatum,
  heute,
  zahlLesen,
  type TabProps,
} from "./teile";

const ART: Record<TagArt, string> = {
  geschaeft: "Geschäftsfahrt",
  pendel: "Fahrt Wohnung–Betrieb",
  homeoffice: "Homeoffice",
};

export const FahrtenTab = ({ basis, daten, neuLaden }: TabProps) => {
  const notify = useNotify();
  const { jahr } = basis;
  const [f, setF] = useState({
    datum: heute(),
    art: "geschaeft" as TagArt,
    km: "",
    notiz: "",
  });
  const summe = pauschalenRechnen(jahr, daten.tage);

  const schreiben = async (
    aktion: () => PromiseLike<{ error: { message: string } | null }>,
  ) => {
    try {
      pruefe(await aktion());
      await neuLaden();
      return true;
    } catch (e) {
      notify(e instanceof Error ? e.message : String(e), { type: "error" });
      return false;
    }
  };

  return (
    <div className="space-y-4">
      <Abschnitt
        titel="Fahrten und Homeoffice"
        text={`Wirken nur auf die EÜR, nicht auf die USt. Ein Tag trägt höchstens eine Art; mehrere Geschäftsfahrten am selben Tag sind möglich.`}
      >
        {jahr.entfernung_km === 0 && (
          <Hinweis art="warnung">
            Entfernung Wohnung–Betrieb fehlt im Setup, Fahrten dorthin zählen
            sonst 0 €.
          </Hinweis>
        )}
        <div className="flex flex-wrap items-end gap-2">
          <Feld name="Datum">
            <Input
              type="date"
              value={f.datum}
              onChange={(e) => setF({ ...f, datum: e.target.value })}
            />
          </Feld>
          <Feld name="Art">
            <Auswahl
              value={f.art}
              onChange={(e) => setF({ ...f, art: e.target.value as TagArt })}
            >
              {Object.entries(ART).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </Auswahl>
          </Feld>
          {f.art === "geschaeft" && (
            <Feld name="Gefahrene km">
              <Input
                inputMode="decimal"
                value={f.km}
                onChange={(e) => setF({ ...f, km: e.target.value })}
              />
            </Feld>
          )}
          <Feld name="Notiz">
            <Input
              value={f.notiz}
              onChange={(e) => setF({ ...f, notiz: e.target.value })}
            />
          </Feld>
          <Button
            disabled={f.art === "geschaeft" && !f.km}
            onClick={async () => {
              const ok = await schreiben(() =>
                sb()
                  .from("bh_tag")
                  .insert({
                    datum: f.datum,
                    art: f.art,
                    km: f.art === "geschaeft" ? zahlLesen(f.km) : null,
                    notiz: f.notiz || null,
                  }),
              );
              if (ok) setF({ ...f, km: "", notiz: "" });
            }}
          >
            Eintragen
          </Button>
        </div>
      </Abschnitt>

      <Abschnitt titel={`Summe ${jahr.jahr}`}>
        <Tabelle
          kopf={["Pauschale", "Menge", "Satz", "Betrag"]}
          rechts={[1, 2, 3]}
        >
          <tr>
            <td>Geschäftsfahrten</td>
            <Zahl>{summe.km.toLocaleString("de-DE")} km</Zahl>
            <Zahl>{jahr.km_satz.toLocaleString("de-DE")} €/km</Zahl>
            <Zahl>{fmtCent(summe.geschaeft)}</Zahl>
          </tr>
          <tr>
            <td>Wohnung–Betrieb</td>
            <Zahl>
              {summe.pendelTage} Tage × {jahr.entfernung_km} km
            </Zahl>
            <Zahl>{jahr.pendel_satz.toLocaleString("de-DE")} €/km</Zahl>
            <Zahl>{fmtCent(summe.pendel)}</Zahl>
          </tr>
          <tr>
            <td>Homeoffice</td>
            <Zahl>{summe.homeTage} Tage</Zahl>
            <Zahl>
              {jahr.homeoffice_tag} €, max.{" "}
              {jahr.homeoffice_max.toLocaleString("de-DE")} €
            </Zahl>
            <Zahl>{fmtCent(summe.homeoffice)}</Zahl>
          </tr>
          <tr className="font-semibold">
            <td>Gesamt</td>
            <Zahl>{""}</Zahl>
            <Zahl>{""}</Zahl>
            <Zahl>{fmtCent(summe.summe)}</Zahl>
          </tr>
        </Tabelle>
      </Abschnitt>

      {daten.tage.length > 0 && (
        <Abschnitt titel="Einträge">
          <Tabelle kopf={["Datum", "Art", "km", "Notiz", ""]} rechts={[2]}>
            {daten.tage.map((t) => (
              <tr key={t.id}>
                <td className="whitespace-nowrap">{fmtDatum(t.datum)}</td>
                <td>{ART[t.art]}</td>
                <Zahl>{t.km ?? ""}</Zahl>
                <td>{t.notiz}</td>
                <td className="text-right">
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label="Eintrag löschen"
                    onClick={() =>
                      schreiben(() =>
                        sb().from("bh_tag").delete().eq("id", t.id),
                      )
                    }
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </td>
              </tr>
            ))}
          </Tabelle>
        </Abschnitt>
      )}
    </div>
  );
};
